"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { ChevronDown } from "lucide-react";
import { useEffect, useRef } from "react";
export default function HeroManifesto() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1,
    once: true
  });
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const setCanvasSize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    setCanvasSize();
    window.addEventListener("resize", setCanvasSize);
    let time = 0;
    const animate = () => {
      if (!ctx || !canvas) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const gradient = ctx.createRadialGradient(canvas.width / 2, canvas.height / 2, 100, canvas.width / 2, canvas.height / 2, Math.max(canvas.width, canvas.height) * 0.6);
      const alpha = 0.15 + Math.sin(time * 0.002) * 0.1;
      gradient.addColorStop(0, `hsla(215, 95%, 58%, ${alpha})`);
      gradient.addColorStop(0.5, `hsla(215, 95%, 58%, ${alpha * 0.5})`);
      gradient.addColorStop(1, "hsla(215, 95%, 58%, 0)");
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      for (let i = 0; i < 3; i++) {
        const offsetX = Math.sin(time * 0.001 + i) * 20;
        const offsetY = Math.cos(time * 0.001 + i) * 20;
        ctx.globalAlpha = 0.03;
        ctx.fillStyle = "hsl(215, 95%, 58%)";
        ctx.fillRect(canvas.width / 2 - 200 + offsetX, canvas.height / 2 - 100 + offsetY, 400, 200);
      }
      ctx.globalAlpha = 1;
      time += 16;
      animationFrameRef.current = requestAnimationFrame(animate);
    };
    animate();
    return () => {
      window.removeEventListener("resize", setCanvasSize);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, []);
  return <section ref={ref} className="relative flex min-h-screen items-center justify-center overflow-hidden bg-background px-4 py-16">
      <canvas ref={canvasRef} className="pointer-events-none absolute inset-0 z-0" aria-hidden="true" />

      <div className="relative z-10 flex max-w-5xl flex-col items-center text-center">
        <div className={`transition-all duration-[800ms] ease-[cubic-bezier(0.4,0,0.2,1)] ${isVisible ? "translate-y-0 opacity-100" : "translate-y-5 opacity-0"}`}>
          <h1 className="mb-6 font-display text-5xl font-bold leading-tight text-foreground md:text-6xl lg:text-7xl">
            Remix Your Workflow.
            <br />
            <span className="bg-gradient-to-r from-primary via-secondary to-accent bg-clip-text text-transparent">
              Reclaim Your Rhythm.
            </span>
          </h1>

          <p className="mx-auto mb-10 max-w-2xl text-lg text-foreground/80 md:text-xl">
            We are Agentic Artist — where cybersecurity meets street-dance culture. Eliminate administrative friction with AI-driven automation systems built for dance studios and nonprofits.
          </p>

          <Link to="https://form.jotform.com/260121991429154">
            <Button size="lg" className="group relative overflow-hidden bg-primary px-8 py-6 text-lg font-semibold text-foreground shadow-strong transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)] hover:scale-105 hover:bg-primary-hover hover:shadow-[0_0_30px_rgba(66,153,225,0.5)] focus-visible:ring-2 focus-visible:ring-primary">
              Book Your Strategy Audit
              <span className="ml-2 inline-block transition-transform duration-300 group-hover:translate-x-1">
                →
              </span>
            </Button>
          </Link>
        </div>
      </div>

      <Link to="#next-section" className="group absolute bottom-8 z-10 flex flex-col items-center gap-2 text-foreground transition-all duration-300 hover:text-primary-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary" aria-label="Scroll down to next section">
        <span className="text-sm font-medium uppercase tracking-wider">
          Scroll
        </span>
        <ChevronDown className="size-6 animate-bounce" />
      </Link>
    </section>;
}